import { useMemo, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { getPdfDownloadUrl } from "@/utils/common";
import { handleError } from "@/utils/error";
import { useDownloadPdfReport } from "web-core/hooks";
import { useGenerateReportFile, webDownload } from "creso-core/hooks";
import type { Client } from "creso-core/types";
import { getFinancialYear } from "creso-core/utils";
import { format, subDays } from "date-fns";
import { InfoIcon } from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  DatePicker as DSDatePicker,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  V2Dialog,
  V2Label,
} from "web-design-system";

const DATE_RANGE_OPTIONS = [
  { label: "Last 7 days", value: "last_7_days" },
  { label: "Last 30 days", value: "last_30_days" },
] as const;

const CUSTOM_DATE_RANGE_OPTION = {
  label: "Custom date range",
  value: "custom",
} as const;

export function DownloadOrderHistory({ client }: { client: Client }) {
  const router = useRouter();

  const [downloadPdfReport, isGenerating] = useDownloadPdfReport();
  const { mutateAsync: generateReportFile, isPending: isGeneratingExcel } =
    useGenerateReportFile<"recent_transactions">();

  const currentFinancialYear = useMemo(() => getFinancialYear(new Date()), []);
  const FY_YEARS = useMemo(
    () => Array.from({ length: 3 }, (_, i) => currentFinancialYear - i),
    [currentFinancialYear]
  );

  const [dateRange, setDateRange] = useState<string>(
    DATE_RANGE_OPTIONS[0].value
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [fileType, setFileType] = useState<"pdf" | "excel">("pdf");

  const isCustomDateRange = dateRange === CUSTOM_DATE_RANGE_OPTION.value;

  const getDateRange = () => {
    const today = format(new Date(), "yyyy-MM-dd");

    if (dateRange === "last_7_days") {
      return {
        start_date: format(subDays(new Date(), 7), "yyyy-MM-dd"),
        end_date: today,
      };
    }

    if (dateRange === "last_30_days") {
      return {
        start_date: format(subDays(new Date(), 30), "yyyy-MM-dd"),
        end_date: today,
      };
    }

    if (isCustomDateRange) {
      if (!fromDate || !toDate) {
        toast.error("Please select both from date and to date");
        return null;
      }

      if (new Date(fromDate) > new Date(toDate)) {
        toast.error("From date should be before to date");
        return null;
      }

      return { start_date: fromDate, end_date: toDate };
    }

    const year = Number(dateRange.replace("fy_", ""));
    return {
      start_date: format(new Date(year, 3, 1), "yyyy-MM-dd"),
      end_date: format(new Date(year + 1, 2, 31), "yyyy-MM-dd"),
    };
  };

  const handleDownload = async () => {
    const range = getDateRange();
    if (!range) return;

    try {
      const fileName = `${client.name}-${format(new Date(), "dd-MM-yyyy")}-order-history`;

      if (fileType === "pdf") {
        const urlSearchParams = new URLSearchParams({
          start_date: range.start_date,
          end_date: range.end_date,
          date_range: dateRange,
        });

        const downloadUrl = getPdfDownloadUrl(
          `/reports/clients/${client.id}/order-history?${urlSearchParams.toString()}`
        );
        await downloadPdfReport(downloadUrl, `${fileName}.pdf`, {
          loading: "Generating order history report",
        });
      } else {
        const result = await generateReportFile({
          file_type: fileType,
          investment_account_guid: client.investment_account ?? "",
          type: "recent_transactions",
          start_date: range.start_date,
          end_date: range.end_date,
          additional_details: {
            order_status: "COMPLETED",
          },
        });
        if (!("report_url" in result) || !result.report_url) {
          throw new Error("Failed to generate order history report");
        }
        const reportUrl = result.report_url;
        const fileExtension = reportUrl.split(".").pop();
        const response = await fetch(
          `${reportUrl}?q=${new Date().toISOString()}`
        );
        if (!response.ok) {
          throw new Error("Failed to generate order history report");
        }
        const blob = await response.blob();
        await webDownload(blob, `${fileName}.${fileExtension}`);
        toast.success("Order history report generated successfully");
      }
    } catch (error) {
      handleError(error, "Failed to generate order history report");
    }
  };

  return (
    <V2Dialog open onOpenChange={(open) => !open && router.history.back()}>
      <DialogContent className="p-6 w-100 gap-5">
        <DialogHeader>
          <DialogTitle className="!text-lg">Download order history</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-1.5 p-3 bg-tag-slate rounded-md">
            <InfoIcon className="size-4 shrink-0 text-tag-slate-foreground" />
            <span className="text-sm text-tag-slate-foreground">
              This will only include completed transactions
            </span>
          </div>

          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center justify-between gap-3">
              <V2Label className="shrink-0 whitespace-nowrap text-sm font-normal">
                Date range
              </V2Label>
              <div className="min-w-0 max-w-56 flex-1">
                <Select value={dateRange} onValueChange={setDateRange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select date range" />
                  </SelectTrigger>
                  <SelectContent>
                    {DATE_RANGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                    {FY_YEARS.map((year) => (
                      <SelectItem key={year} value={`fy_${year}`}>
                        Apr {year} - Mar {year + 1}
                      </SelectItem>
                    ))}
                    <SelectItem value={CUSTOM_DATE_RANGE_OPTION.value}>
                      {CUSTOM_DATE_RANGE_OPTION.label}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isCustomDateRange ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="min-w-0">
                  <DSDatePicker
                    value={fromDate}
                    onChange={setFromDate}
                    placeholder="From date"
                    yearRange={{
                      from: currentFinancialYear - 2,
                      to: currentFinancialYear,
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <DSDatePicker
                    value={toDate}
                    onChange={setToDate}
                    placeholder="To date"
                    yearRange={{
                      from: currentFinancialYear - 2,
                      to: currentFinancialYear,
                    }}
                    disabled={(date: Date) =>
                      fromDate ? date < new Date(fromDate) : false
                    }
                  />
                </div>
              </div>
            ) : null}
          </div>

          <div className="flex flex-row items-center justify-between gap-3">
            <V2Label className="shrink-0 whitespace-nowrap text-sm font-normal">
              File type
            </V2Label>
            <div className="min-w-0 max-w-56 flex-1">
              <Tabs
                value={fileType}
                onValueChange={(value) => setFileType(value as "pdf" | "excel")}
              >
                <TabsList className="w-full">
                  <TabsTrigger value="pdf" className="flex-1">
                    PDF
                  </TabsTrigger>
                  <TabsTrigger value="excel" className="flex-1">
                    Excel
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            className="w-full"
            onClick={handleDownload}
            disabled={isGenerating || isGeneratingExcel}
          >
            {isGenerating || isGeneratingExcel ? "Generating..." : "Download"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </V2Dialog>
  );
}
